import { useMemo } from 'react'
import { useLocationAccess } from './useLocationAccess'
import { useAppContext } from './routing'

interface LocationDisplay {
  /** Human-readable facility name, e.g. "Augusta Rgnl At Bush Fld". */
  name: string | null
  /** Best available identifier — ICAO, falling back to IATA then the raw code. */
  code: string | null
}

/**
 * Name + airport identifier for the location currently in scope.
 *
 * Reads from TenantContext (already loaded), so it costs no fetch. Prefers the
 * ICAO `airport_code` — that's the identifier operators recognize on a
 * zoomed-out map — where CommandIsland deliberately shows the shorter IATA.
 */
export function useLocationDisplay(): LocationDisplay {
  const { locationId, locationName } = useAppContext()
  const { accessibleLocations } = useLocationAccess()

  return useMemo(() => {
    const current = accessibleLocations.find((loc) => loc.id === locationId)
    return {
      name: locationName ?? current?.name ?? null,
      code: current?.airport_code || current?.iata_code || current?.location_code || null,
    }
  }, [accessibleLocations, locationId, locationName])
}
